import { isString } from './type-util';

export const NUMBERS = '0123456789';
export const ALPHABET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const NUMBERS_ALPHABET = NUMBERS + ALPHABET;

// Bullet-like characters our database doesn't like which can be replaced by bullet characters our database does like
const INVALID_CHARACTERS_REPLACE_BULLETS = new RegExp(
  `[${['\u{2027}', '\u{22C5}', '\u{25A0}-\u{25FF}'].join('')}]`,
  'g'
);

// Hyphen-like characters our database doesn't like which can be replaced by hyphen characters our database does like
const INVALID_CHARACTERS_REPLACE_HYPHENS = new RegExp(
  `[${['\u{1806}', '\u{2010}-\u{2013}', '\u{2043}', '\u{2212}', '\u{FE63}', '\u{FF0D}'].join('')}]`,
  'g'
);

/**
 * Using characters noted here, except space, tab, and newline:
 * https://github.com/flopp/invisible-characters/blob/001e81dfe1ef09304a055fe12e0795f6c7321ac6/characters.json
 *
 * Additionally, we also exclude some of the weird C0 ones noted here:
 * https://en.wikipedia.org/wiki/C0_and_C1_control_codes
 */
// eslint-disable-next-line no-misleading-character-class
const INVALID_CHARACTERS = new RegExp(
  `[${[
    '\u{0000}-\u{0008}',
    '\u{000B}',
    '\u{000C}',
    '\u{000E}-\u{001F}',
    '\u{007F}',
    '\u{00A0}',
    '\u{00AD}',
    '\u{034F}',
    '\u{061C}',
    '\u{115F}',
    '\u{1160}',
    '\u{17B4}',
    '\u{17B5}',
    '\u{180E}',
    '\u{2000}',
    '\u{2001}',
    '\u{2002}',
    '\u{2003}',
    '\u{2004}',
    '\u{2005}',
    '\u{2006}',
    '\u{2007}',
    '\u{2008}',
    '\u{2009}',
    '\u{200A}',
    '\u{200B}',
    '\u{200C}',
    '\u{200D}',
    '\u{200E}',
    '\u{200F}',
    '\u{2028}-\u{202F}',
    '\u{205F}',
    '\u{2060}',
    '\u{2061}',
    '\u{2062}',
    '\u{2063}',
    '\u{2064}',
    '\u{206A}',
    '\u{206B}',
    '\u{206C}',
    '\u{206D}',
    '\u{206E}',
    '\u{206F}',
    '\u{3000}',
    '\u{2800}',
    '\u{3164}',
    '\u{E000}-\u{F8FF}',
    '\u{FEFF}',
    '\u{FFA0}',
    '\u{1D159}',
    '\u{1D173}',
    '\u{1D174}',
    '\u{1D175}',
    '\u{1D176}',
    '\u{1D177}',
    '\u{1D178}',
    '\u{1D179}',
    '\u{1D17A}'
  ].join('')}]`,
  'g'
);

export function randomString(length: number, chars: string) {
  let result = '';

  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }

  return result;
}

export function randomNumericString(length: number) {
  return randomString(length, NUMBERS);
}

export function randomAlphaString(length: number) {
  return randomString(length, ALPHABET);
}

export function randomAlphanumericString(length: number) {
  return randomString(length, NUMBERS_ALPHABET);
}

/**
 * Allows substituting values in normal strings as if they were
 * ES6 template strings.
 *
 * For example:
 * ```
 * const templateString = 'My name is ${name}';
 * const filledTemplateString = fillTemplate(templateString, { name: 'John Doe' });
 * console.log(filledTemplateString); // Prints 'My name is John Doe'
 * ```
 * @param templateString Any normal JS string with `${...}` placeholders
 * @param templateParams An object containing the key-value pairs to substitute placeholder values
 */
export function fillTemplate(templateString: string, templateParams: { [key: string]: unknown }): string {
  const templateFn = new Function(...Object.keys(templateParams), 'return `' + templateString + '`;');
  return templateFn(...Object.values(templateParams));
}

export function removeInvalidCharacters(val: string): string {
  if (isString(val)) {
    let newVal = val?.replaceAll?.(INVALID_CHARACTERS, '')?.trim();
    newVal = newVal?.replaceAll?.(INVALID_CHARACTERS_REPLACE_BULLETS, '\u{2022}')?.trim();
    newVal = newVal?.replaceAll?.(INVALID_CHARACTERS_REPLACE_HYPHENS, '-')?.trim();

    return newVal;
  }

  return val;
}
